import React from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  stopPropagation?: boolean;
  badge?: boolean | string;
  badgeClassName?: string;
  href?: string;
};

const Button: React.FC<ButtonProps> = ({
  className,
  type = 'button',
  children,
  onClick: onExternalClick,
  loading,
  badge,
  badgeClassName,
  stopPropagation = true,
  href,
  ...props
}) => {
  const router = useRouter();
  const onClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (props.disabled) {
      return;
    }

    if (href) {
      // If the href is an external URL (starts with http), open it in a new tab
      if (href.startsWith('http')) {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      router.push(href);
      return;
    }

    if (!onExternalClick) {
      return;
    }

    onExternalClick?.(e);
  };

  return (
    <button onClick={onClick} type={type} className={classNames('btn', { relative: !!badge, 'btn-disabled': props.disabled }, className)} {...props}>
      {badge && (
        <div
          className={classNames(
            'badge badge-sm absolute right-[-5px] top-[-5px]',
            {
              'badge-accent animate-[ping_1s_ease-in-out_infinite]': !badgeClassName,
            },
            badgeClassName
          )}
        >
          {typeof badge === 'string' ? badge : ''}
        </div>
      )}
      {loading && <span className="loading loading-xs"></span>}
      {children}
    </button>
  );
};

export default Button;
